import { Screen, useNavigate } from "flemo";
import { LoaderCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";

import appleIcon from "@/assets/apple.svg";
import googleIcon from "@/assets/google.svg";
import kakaoIcon from "@/assets/kakao.svg";
import Logo from "@/assets/logo.svg";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trackEvent } from "@/lib/analytics";
import { AuthStatus } from "@/lib/constant";
import { logger } from "@/lib/logger";
import { isNativeApp, nativeBridge } from "@/lib/native";
import { supabase } from "@/lib/supabaseClient";
import { fadeTransition } from "@/lib/transitions";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";

type SignInProvider = "google" | "apple" | "kakao";

export function LoginPage() {
  const [loadingProvider, setLoadingProvider] = useState<SignInProvider | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const { push, replace } = useNavigate();
  const status = useAuthStore(state => state.status);

  useEffect(() => {
    if (status === AuthStatus.AUTHENTICATED) {
      replace("/", {}, { transitionName: "none" });
    }
  }, [status]);

  const signIn = async (provider: SignInProvider) => {
    setAuthError(null);
    trackEvent("signin_initiated", { provider });
    setLoadingProvider(provider);

    if (isNativeApp) {
      try {
        const result = await nativeBridge.auth.signInWithOAuth(provider);

        if (result) {
          await supabase.auth.setSession({
            access_token: result.accessToken,
            refresh_token: result.refreshToken,
          });
        } else {
          trackEvent("signin_failed", { provider, error_type: "no_result" });
          setAuthError("로그인에 실패했어요. 다시 시도해 주세요.");
        }
      } catch {
        trackEvent("signin_failed", { provider, error_type: "native_exception" });
        setAuthError("로그인에 실패했어요. 다시 시도해 주세요.");
      } finally {
        setLoadingProvider(null);
      }
    } else {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
      });

      if (error) {
        trackEvent("signin_failed", { provider, error_type: "oauth_error" });
        setAuthError("로그인에 실패했어요. 다시 시도해 주세요.");
        logger.error({ domain: "auth", message: "OAuth 로그인 실패", error, data: { provider } });
      }

      setLoadingProvider(null);
    }
  };

  const isAnyLoading = loadingProvider !== null;

  return (
    <Screen>
      <main className="md:bg-muted flex h-dvh flex-col md:flex-row items-stretch md:items-center justify-center bg-cover md:h-auto md:min-h-screen pt-safe md:pb-safe">
        <div className="flex flex-1 w-full flex-col md:flex-initial md:h-auto md:max-w-sm">
          <Card className="flex-1 md:flex-initial md:h-auto rounded-none md:rounded-xl gap-y-6 justify-center md:justify-start">
            <CardHeader className="flex flex-col items-center gap-2">
              {/* 로고 */}
              <CardTitle>
                <img src={Logo} alt="Logo" className="h-32 w-32" />
              </CardTitle>
            </CardHeader>

            {/* 로그인 버튼 */}
            <CardContent className="flex w-full flex-col gap-y-3">
              <Button
                size="lg"
                type="button"
                className="w-full border-transparent bg-[#FEE500] py-5 text-black hover:bg-[#FEE500]/90"
                onClick={() => signIn("kakao")}
                disabled={isAnyLoading}
              >
                {loadingProvider === "kakao" && <LoaderCircleIcon className={cn("h-4 w-4 animate-spin")} />}
                <img src={kakaoIcon} alt="Kakao" className="h-4 w-4" />
                카카오로 시작하기
              </Button>
              <Button
                size="lg"
                type="button"
                variant="outline"
                className="w-full py-5"
                onClick={() => signIn("google")}
                disabled={isAnyLoading}
              >
                {loadingProvider === "google" && <LoaderCircleIcon className={cn("h-4 w-4 animate-spin")} />}
                <img src={googleIcon} alt="Google" className="h-4 w-4" />
                Google로 시작하기
              </Button>
              <Button
                size="lg"
                type="button"
                className="w-full border-transparent bg-[#050708] py-5 text-white hover:bg-[#050708]/90"
                onClick={() => signIn("apple")}
                disabled={isAnyLoading}
              >
                {loadingProvider === "apple" && <LoaderCircleIcon className={cn("h-4 w-4 animate-spin")} />}
                <img src={appleIcon} alt="Apple" className="h-4 w-4 invert" />
                Apple로 시작하기
              </Button>

              {/* 에러 메시지 */}
              {authError ? <p className="text-center text-sm text-red-500">{authError}</p> : null}
            </CardContent>
          </Card>

          {/* 푸터 */}
          <footer className="bg-muted text-muted-foreground mt-auto flex w-full flex-col gap-y-4 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] text-center text-sm md:flex-row md:justify-between md:bg-transparent">
            <div>© {new Date().getFullYear()} Case :D</div>
            <div>
              <Button
                variant="ghost"
                type="button"
                onClick={() => push("/terms", {}, { transitionName: fadeTransition.name })}
              >
                Terms of Use
              </Button>
              |
              <Button
                variant="ghost"
                type="button"
                onClick={() => push("/privacy", {}, { transitionName: fadeTransition.name })}
              >
                Privacy Policy
              </Button>
            </div>
          </footer>
        </div>
      </main>
    </Screen>
  );
}
